import React, { Suspense, useEffect, memo, useState } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import { initializeAnalytics } from "./utils/analytics";
import {
  getConsentPreferences,
  acceptAllCookies,
  rejectAllCookies,
  migrateLegacyConsent,
  ConsentPreferences
} from "./utils/consentManager";
import CookiePreferencesModal from "./components/CookiePreferencesModal";

import Homepage from "./pages/HomePage";

interface LazyFactory {
  (): Promise<{ default: React.ComponentType }>;
}

const lazyWithFallback = (factory: LazyFactory): React.LazyExoticComponent<React.ComponentType> =>
  React.lazy(() =>
    factory().catch(() => {
      return { default: () => <NotFound /> };
    })
  );

const Background = lazyWithFallback(() => import("./components/layout/Background"));
const Welcome = lazyWithFallback(() => import("./pages/Welcome"));
const Contact = lazyWithFallback(() => import("./pages/Contact"));
const PrivacyPolicy = lazyWithFallback(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithFallback(() => import("./pages/TermsOfService"));
const StatusPage = lazyWithFallback(() => import("./pages/StatusPage"));
const SuccessfullyBought = lazyWithFallback(() => import("./pages/SuccessfullyBought"));
const RefundPolicyAndCancellation = lazyWithFallback(() => import("./pages/Refund"));
const Vote = lazyWithFallback(() => import("./pages/Vote"));
const TimeZone = lazyWithFallback(() => import("./pages/TimeZone"));
const Roadmap = lazyWithFallback(() => import("./pages/Roadmap"));
const Invite = lazyWithFallback(() => import("./pages/Invite"));
const Support = lazyWithFallback(() => import("./pages/Support"));
const Changelogs = lazyWithFallback(() => import("./pages/Changelogs"));
const Commands = lazyWithFallback(() => import("./pages/Commands"));
const Blog = lazyWithFallback(() => import("./pages/Blog"));
const CookiePolicy = lazyWithFallback(() => import("./pages/CookiePolicy"));
const Faq = lazyWithFallback(() => import("./pages/FAQ"));
const Rules = lazyWithFallback(() => import("./pages/Rules"));
const Team = lazyWithFallback(() => import("./pages/Team"));
const NotFound = lazyWithFallback(() => import("./pages/NotFound"));
const Pricing = lazyWithFallback(() => import("./pages/Pricing"));
const CaptchaPage = lazyWithFallback(() => import("./pages/Captcha"));
// const AIChatbot = lazyWithFallback(() => import("./pages/AiChatBot"));
const Features = lazyWithFallback(() => import("./pages/Features"));
const DMCA = lazyWithFallback(() => import("./pages/DMCA"));
const GDPR = lazyWithFallback(() => import("./pages/GDPR"));
const CCPA = lazyWithFallback(() => import("./pages/CCPA"));
const AUP = lazyWithFallback(() => import("./pages/AUP"));
const DataRequest = lazyWithFallback(() => import("./pages/DataRequest"));

const routeConfig = {
  "/": { component: Homepage, redirects: ["/home", "/homepage", "/index"] },
  "/contact": { component: Contact, redirects: ["/contactus", "/contact-us"] },
  "/changelogs": { component: Changelogs, redirects: ["/changelog"] },
  "/commands": { component: Commands, redirects: ["/command"] },
  "/features": { component: Features, redirects: ["/feature"] },
  "/rules": { component: Rules, redirects: ["/rule"] },
  "/timezone": { component: TimeZone, redirects: ["/timezones"] },
  "/privacy": { component: PrivacyPolicy, redirects: ["/privacypolicy", "/privacy-policy"] },
  "/tos": { component: TermsOfService, redirects: ["/terms-of-service", "/terms-of-services", "/termsofservice", "/termsofservices"] },
  "/cookie-policy": { component: CookiePolicy, redirects: ["/cookiepolicy"] },
  "/welcome": { component: Welcome, redirects: ["/welcomes"] },
  "/blog": { component: Blog, redirects: ["/blogs"] },
  "/status": { component: StatusPage, redirects: ["/statuses"] },
  "/faq": { component: Faq, redirects: ["/faqs"] },
  "/premium": { component: Pricing, redirects: ["/prices", "/price", "/pricing", "/store"] },
  "/team": { component: Team, redirects: ["/teams"] },
  "/vote": { component: Vote, redirects: ["/votes", "/voting"] },
  "/invite": { component: Invite, redirects: ["/addme"] },
  "/support": { component: Support, redirects: ["/help"] },
  "/refund": { component: RefundPolicyAndCancellation, redirects: ["/refunds"] },
  "/successfully-bought": { component: SuccessfullyBought, redirects: ["/bought-successfully"] },
  "/roadmap": { component: Roadmap, redirects: ["/roadmaps"] },
  // "/aichatbot": { component: AIChatbot, redirects: ["/aichatbots"] },
  "/dmca": { component: DMCA, redirects: ["/dmca"] },
  "/gdpr": { component: GDPR, redirects: ["/gdpr"] },
  "/ccpa": { component: CCPA, redirects: ["/ccpa"] },
  "/aup": { component: AUP, redirects: ["/acceptable-use-policy"] },
  "/captcha": { component: CaptchaPage, redirects: ["/captcha-page"] },
  "/data-request": { component: DataRequest, redirects: ["/datarequest", "/request-data", "/requestdata"] },
};

const routes = (() => {
  const routeEntries = Object.entries(routeConfig);
  const generatedRoutes = routeEntries.flatMap(([path, { component: Component, redirects }]) => [
    <Route key={path} path={path} element={<Component />} />,
    ...redirects.map((redirectPath) => (
      <Route key={redirectPath} path={redirectPath} element={<Navigate to={path} replace />} />
    )),
  ]);
  generatedRoutes.push(<Route key="404" path="*" element={<NotFound />} />);
  return generatedRoutes;
})();

const CookieConsentBanner = ({
  onAccept,
  onReject,
  onManagePreferences
}: {
  onAccept: () => void;
  onReject: () => void;
  onManagePreferences: () => void;
}) => (
  <div style={{
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    maxWidth: '420px',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    zIndex: 9999,
  }}
    className="dark:bg-gray-800 dark:border-gray-700"
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
      <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0, color: '#000' }} className="dark:text-white">
        We use cookies
      </h3>
      <button
        onClick={onReject}
        style={{
          background: 'none',
          border: 'none',
          fontSize: '1.5rem',
          cursor: 'pointer',
          padding: '0',
          marginLeft: '1rem',
          lineHeight: 1,
          color: '#6b7280',
          transition: 'color 0.2s',
        }}
        className="hover:text-gray-900 dark:hover:text-white"
        aria-label="Reject all cookies and close"
        title="Reject all cookies"
      >
        ×
      </button>
    </div>
    <p style={{ fontSize: '0.875rem', lineHeight: '1.6', marginBottom: '1rem' }} className="text-gray-600 dark:text-gray-400">
      We use cookies to enhance your experience. Essential cookies are always active.
      You can choose to accept analytics cookies to help us improve our service.
    </p>
    <div style={{ marginBottom: '1rem' }}>
      <a
        href="/cookie-policy"
        style={{ fontSize: '0.875rem', textDecoration: 'underline' }}
        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
      >
        Read our Cookie Policy
      </a>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
      <button
        onClick={onAccept}
        style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#16a34a',
          color: '#fff',
          border: 'none',
          borderRadius: '6px',
          fontSize: '0.875rem',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#15803d'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
      >
        ✓ Accept All Cookies
      </button>
      <div style={{ display: 'flex', gap: '0.625rem' }}>
        <button
          onClick={onReject}
          style={{
            flex: 1,
            padding: '0.75rem 1rem',
            backgroundColor: '#f3f4f6',
            color: '#1f2937',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
          className="dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          Reject All
        </button>
        <button
          onClick={onManagePreferences}
          style={{
            flex: 1,
            padding: '0.75rem 1rem',
            backgroundColor: '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
        >
          Manage Preferences
        </button>
      </div>
    </div>
  </div>
);

const App = memo(() => {
  const [showConsent, setShowConsent] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);

  useEffect(() => {
    migrateLegacyConsent();

    const consent = getConsentPreferences();

    if (consent) {
      if (consent.analytics) {
        if (window.gtag) {
          window.gtag('consent', 'update', {
            analytics_storage: 'granted',
          });
        }
        initializeAnalytics();
      }
    } else {
      setShowConsent(true);
    }
  }, []);

  const handleAccept = () => {
    acceptAllCookies();
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
      });
    }
    initializeAnalytics();
    setShowConsent(false);
  };

  const handleReject = () => {
    rejectAllCookies();
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
      });
    }
    setShowConsent(false);
  };

  const handleManagePreferences = () => {
    setShowConsent(false);
    setShowPreferencesModal(true);
  };

  const handlePreferencesSaved = (preferences: Omit<ConsentPreferences, 'timestamp'>) => {
    if (preferences.analytics) {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
        });
      }
      initializeAnalytics();
    }
  };

  return (
    <ThemeProvider>
      <Router>
        <div className="flex flex-col min-h-screen bg-white dark:bg-black text-black dark:text-white">
          <Navbar />
          <Suspense fallback={<LoadingFallback />}>
            <Background />
            <main className="flex-grow container mx-auto px-4 pt-16">
              <Routes>{routes}</Routes>
            </main>
          </Suspense>
          <Footer />
          {showConsent && (
            <CookieConsentBanner
              onAccept={handleAccept}
              onReject={handleReject}
              onManagePreferences={handleManagePreferences}
            />
          )}
          <CookiePreferencesModal
            isOpen={showPreferencesModal}
            onClose={() => setShowPreferencesModal(false)}
            onSave={handlePreferencesSaved}
          />
        </div>
      </Router>
    </ThemeProvider>
  );
});

// Separate loading component for better organization
const LoadingFallback = () => (
  <div className="flex-1 flex items-center justify-center">Loading...</div>
);

export default App;